/**
 * Bounded list renderer — TASK 8.1 Step 5 (AC 4.3 "all lists bounded").
 *
 * The mock rendered a FIXED window and grew it with a "Load more" button
 * (`state.artShown` + `moreArtists`, L1034). That is the right shape, but the
 * mock hard-coded the window per surface and had no notion of a SERVER page —
 * `/browse/artists` really is 1,010 rows and `/genres/:id` really is 475, and
 * both now arrive one cursor page at a time (`nextCursor`, dto.ts).
 *
 * So this component owns two independent bounds and nothing else:
 *
 *   1. THE RENDER WINDOW. At most `windowSize` items are in the DOM after the
 *      first render, whatever the length of `items`. A 10,000-item array
 *      produces 40 nodes. This is the bound the AC is about: the DOM never
 *      grows because DATA grew, only because the USER asked.
 *   2. THE FETCH. When the rendered window reaches the end of the items we
 *      already hold AND the server says there is more (`hasMore`), the same
 *      button calls `onLoadMore()` — the caller fetches the next cursor page
 *      and calls {@link VirtualListHandle.update} with the longer array.
 *
 * NOT windowing-on-scroll. jsdom performs no layout, so a scroll-driven
 * windowing implementation could not be tested here at all (every
 * `getBoundingClientRect()` is 0×0 — see apps/web/test/miniplayer-dock.test.ts
 * for why that matters), and ui-contract §10.6's scale table specifies exactly
 * this behaviour anyway: "/browse/artists · 1,010 artists · initial render
 * window 40 · Load more artists ↓ · +40".
 *
 * PAINTING IS INCREMENTAL. Growing the window (or appending a fetched page)
 * appends ONLY the newly revealed rows; the rows already on screen keep their
 * DOM nodes, so scroll anchoring and focus inside the list survive a "Load
 * more" and the total render work over N pages is O(N), not O(N²). A full
 * repaint happens only when the data underneath the window actually changed
 * (detected by item identity, so a fresh non-append load repaints).
 *
 * @module
 */

/* global IntersectionObserver -- browser platform API; eslint.config.js's
   browser globals block predates this component's auto-load observer (the same
   gap QueueView.js notes for AbortController) */

import { append, classes, el } from './primitives/dom.js';

/** ui-contract §10.6 scale table — grids/card lists page 40 at a time. */
export const DEFAULT_WINDOW_SIZE = 40;

/**
 * @template T
 * @typedef {object} VirtualListHandle
 * @property {HTMLElement} element the container (list + pagination control)
 * @property {(next: { items?: T[], hasMore?: boolean, busy?: boolean }) => void} update
 * @property {() => number} renderedCount how many items are currently in the DOM
 * @property {() => number} shownCount how far the render WINDOW has been opened
 *   — which is `renderedCount` plus however much of the window the data in hand
 *   has not filled yet. This is the number worth remembering across a
 *   navigation (`lib/list-depth.ts`), because it is what the user actually grew.
 * @property {() => void} dispose stop the auto-load observer (call on teardown)
 */

/**
 * @template T
 * @param {{
 *   items?: T[],
 *   renderItem: (item: T, index: number) => Node|null,
 *   windowSize?: number,
 *   increment?: number,
 *   hasMore?: boolean,
 *   onLoadMore?: () => void,
 *   loadMoreLabel?: string|((info: { remaining: number }) => string),
 *   loadMoreTestId?: string,
 *   listClassName?: string|string[],
 *   listTag?: string,
 *   initialShown?: number,
 *   onWindowChange?: (shown: number) => void,
 *   autoLoad?: boolean,
 *   createObserver?: (cb: (entries: { isIntersecting: boolean }[]) => void) => {
 *     observe: (el: Element) => void, disconnect: () => void
 *   } | null,
 * }} options
 *   `loadMoreLabel` may be a function of the rows still IN HAND but not yet
 *   rendered (ui-contract §10.6's ordered-list "Show more (N more)" variant).
 *   It deliberately receives no server-side total — a keyset page does not
 *   know one, so a label built on it would be a guess.
 * @returns {VirtualListHandle<T>}
 */
export function VirtualList(options) {
  const {
    renderItem,
    windowSize = DEFAULT_WINDOW_SIZE,
    increment = windowSize,
    loadMoreLabel = 'Load more ↓',
    loadMoreTestId,
    listClassName = 'sm-stack-8',
    listTag = 'div',
    // #back — re-open the window a returning user had already grown, so the
    // list is as tall as they left it and their scroll position is reachable.
    // Never SHRINKS the window: a value below `windowSize` is the default.
    initialShown,
    onWindowChange,
    onLoadMore,
    // Feature #3 — auto-load when the "load more" control scrolls into view.
    // A progressive enhancement: the button still works, and where
    // IntersectionObserver is absent (older engines, jsdom) it is simply a
    // no-op. `rootMargin` pre-fetches a screenful early so the next rows are
    // usually there before the user reaches the bottom.
    autoLoad = true,
    createObserver = (cb) =>
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(cb, { rootMargin: '400px 0px' }),
  } = options;

  if (windowSize < 1) throw new Error('VirtualList: windowSize must be >= 1');

  /** @type {T[]} */
  let items = options.items ?? [];
  let hasMore = options.hasMore ?? false;
  let busy = false;
  /** How many items are allowed in the DOM right now. */
  let shown = Math.max(windowSize, Math.floor(initialShown ?? 0));
  /**
   * The exact items (by identity, in order) the current DOM rows were painted
   * from. This is what lets {@link paint} tell "the window grew" (append only
   * the new slice) apart from "the data changed" (full repaint).
   *
   * @type {T[]}
   */
  let painted = [];

  const root = el('div');
  const list = el(listTag, {
    className: classes(...(Array.isArray(listClassName) ? listClassName : [listClassName])),
  });

  /**
   * Reveal the next window — from rows already in hand, or by asking the caller
   * for a server page. Shared by the button click and the scroll observer.
   */
  function triggerMore() {
    if (busy) return;
    if (shown < items.length) {
      // More already in hand: widen the window, no network.
      shown += increment;
      paint();
      onWindowChange?.(shown);
      return;
    }
    // Window is at the end of what we hold — ask the caller for a page.
    if (hasMore && onLoadMore !== undefined) {
      busy = true;
      shown += increment;
      paint();
      onWindowChange?.(shown);
      onLoadMore();
    }
  }

  const loadMore = el('button', {
    className: 'sm-btn sm-btn--panel sm-btn--block',
    testId: loadMoreTestId,
    attrs: { type: 'button', hidden: true },
    on: { click: triggerMore },
  });

  append(root, list, loadMore);

  // Auto-load: when the (visible) load-more control enters view, advance. A
  // `hidden` control has no box and never intersects, so once everything is
  // shown this stops firing on its own.
  const observer = autoLoad
    ? createObserver((entries) => {
        for (const entry of entries) if (entry.isIntersecting) triggerMore();
      })
    : null;
  if (observer !== null) observer.observe(loadMore);

  function paint() {
    const visible = items.slice(0, Math.min(shown, items.length));

    // How many already-rendered rows were painted from the SAME item (by
    // identity)? Append pages spread the old array into the new one, so a
    // grown window or an appended cursor page keeps its whole prefix — and a
    // fresh load (new item objects) keeps none.
    let kept = 0;
    while (kept < painted.length && kept < visible.length && painted[kept] === visible[kept]) {
      kept += 1;
    }

    /** @type {Node[]} */
    const nodes = [];
    for (let i = kept === painted.length ? kept : 0; i < visible.length; i += 1) {
      const node = renderItem(/** @type {T} */ (visible[i]), i);
      if (node !== null && node !== undefined) nodes.push(node);
    }
    if (kept === painted.length) {
      // Pure growth: the rows on screen keep their DOM nodes (scroll anchoring
      // and focus survive), and only the newly revealed slice is built.
      list.append(...nodes);
    } else {
      list.replaceChildren(...nodes);
    }
    painted = visible;

    const more = shown < items.length || hasMore;
    loadMore.textContent =
      typeof loadMoreLabel === 'function'
        ? loadMoreLabel({ remaining: items.length - visible.length })
        : loadMoreLabel;
    if (more) loadMore.removeAttribute('hidden');
    else loadMore.setAttribute('hidden', '');
    if (busy) loadMore.setAttribute('disabled', '');
    else loadMore.removeAttribute('disabled');
  }

  paint();

  return {
    element: root,
    update(next) {
      if (next.items !== undefined) items = next.items;
      if (next.hasMore !== undefined) hasMore = next.hasMore;
      if (next.busy !== undefined) busy = next.busy;
      else busy = false;
      paint();
    },
    renderedCount: () => list.children.length,
    shownCount: () => shown,
    dispose: () => observer?.disconnect(),
  };
}
